/**
 * Recent Players Store
 *
 * Tracks recently viewed players for quick access in create menus.
 * @module stores/recentPlayers
 */

import { makeVar } from "@apollo/client";

/**
 * Represents a recently viewed player
 */
export interface RecentPlayer {
  id: string;
  /** Backend-canonical display name (SE-5469). */
  name: string;
  firstName: string;
  lastName: string;
  viewedAt: number;
}

/**
 * Maximum number of recent players to track
 */
export const MAX_RECENT_PLAYERS = 5;

/**
 * Reactive variable for recent players list
 */
export const recentPlayersVar = makeVar<readonly RecentPlayer[]>([]);

/**
 * Remove a player from the recent players list by ID.
 * Called after a manual player is deleted so the global "+" menu no longer
 * shows a zombie entry that links to a non-existent profile.
 * @param playerId - ID of the player to remove
 */
export const removeRecentPlayer = (playerId: string) => {
  recentPlayersVar(recentPlayersVar().filter(p => p.id !== playerId));
};

/**
 * Add a player to recent players list
 * @param player - Player to add
 */
export const addRecentPlayer = (player: Omit<RecentPlayer, "viewedAt">) => {
  const current = recentPlayersVar();

  // Remove if already exists (will re-add at top)
  const filtered = current.filter(p => p.id !== player.id);

  // Add to beginning with current timestamp
  const updated = [{ ...player, viewedAt: Date.now() }, ...filtered].slice(
    0,
    MAX_RECENT_PLAYERS
  );

  recentPlayersVar(updated);
};
